// A compact "workforce bridge" section that positions Freedom In Me as working
// both sides of the workforce equation — the adult AI Build Lab monthly (this
// page's focal product) AND the four broader AI Build Lab pillars: Nonprofits,
// Schools, Small Businesses, and Executive Briefing. Kept intentionally BELOW
// the primary $249 conversion so it doesn't pull focus.
import { Link } from "react-router-dom";
import {
  ArrowUpRight, Briefcase, GraduationCap,
  HeartHandshake, School, Store, LineChart,
} from "lucide-react";

const PILLARS = [
  {
    key: "nonprofits",
    Icon: HeartHandshake,
    eyebrow: "Prepare people",
    title: "AI for Nonprofits",
    body: "Amplify mission and impact. Streamline operations, improve fundraising, and communicate impact more effectively with practical AI.",
    to: "/organization-inquiry",
    accent: "brand",
  },
  {
    key: "schools",
    Icon: School,
    eyebrow: "Equip students",
    title: "AI for Schools",
    body: "Empower educators. Inspire students. Build AI literacy and future-ready skills across teacher training, administration, and the classroom.",
    to: "/student-camp",
    accent: "brand",
  },
  {
    key: "small-business",
    Icon: Store,
    eyebrow: "Empower employers",
    title: "AI for Small Businesses",
    body: "Automate repetitive tasks, improve customer experience, and make smarter decisions with simple, affordable AI built for small teams.",
    to: "/organization-inquiry",
    accent: "brand",
  },
  {
    key: "executive-briefing",
    Icon: LineChart,
    eyebrow: "Strengthen leadership",
    title: "AI Executive Briefing",
    body: "Strategic insights. Real impact. Executive-level AI planning covering trends, risk, roadmap, and measurable ROI for forward-thinking leaders.",
    to: "/organization-inquiry",
    accent: "brand",
  },
];

export default function WorkforceBridge() {
  return (
    <section
      id="workforce-bridge"
      data-testid="workforce-bridge-section"
      className="relative py-24 lg:py-32 bg-bone border-t border-[var(--line)]"
    >
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="max-w-3xl">
          <div className="text-[10px] uppercase font-bold tracking-[0.22em] mb-4 text-brand">The bigger picture</div>
          <h2
            className="font-black uppercase leading-[1.02] tracking-[-0.025em] text-ink"
            style={{ fontSize: "clamp(32px, 5vw, 60px)", fontFamily: "'Inter', -apple-system, 'Segoe UI', system-ui, sans-serif" }}
          >
            Bridging the gap between the workforce{" "}
            <span className="text-brand">employers need</span>{" "}
            &mdash; and the students preparing to enter it.
          </h2>
          <p className="mt-6 text-[16px] leading-[1.65] text-ink/70 max-w-2xl">
            The monthly AI Build Lab above is our flagship offering. Alongside it, Freedom In Me
            works with schools, nonprofits, small businesses, and leadership teams to prepare their
            people for the AI economy.
          </p>
        </div>

        {/* Two-track cards — adult lab + student camp */}
        <div className="mt-12 grid lg:grid-cols-2 gap-5 lg:gap-6">
          <div
            className="rounded-2xl bg-white border border-[var(--line)] p-7 sm:p-8 relative overflow-hidden"
            data-testid="wb-adult-track"
          >
            <div className="absolute top-0 left-0 h-1 w-full bg-brand" />
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 rounded-lg bg-brand-soft grid place-items-center">
                <Briefcase size={19} className="text-brand" strokeWidth={2.1} />
              </div>
              <div>
                <div className="eyebrow !text-brand !mb-0">For adults &amp; teams</div>
                <div className="font-display text-[20px] tracking-[-0.02em] text-ink leading-tight mt-0.5">
                  Monthly AI Build Lab &mdash; where you are now.
                </div>
              </div>
            </div>
            <p className="text-[15px] leading-[1.65] text-ink/70 mb-5">
              Non-technical founders, professionals, and consultants use AI &amp; no-code tools to
              ship websites, apps, dashboards, and workflows live &mdash; with real-time help.
            </p>
            <ul className="space-y-1.5 text-[13.5px] text-ink/70 mb-6">
              <li>&bull; Live 2.5-hour AI Build Lab, once a month</li>
              <li>&bull; Klarna &amp; Afterpay at checkout</li>
              <li>&bull; Custom in-house labs for org teams</li>
            </ul>
            <a href="#offer" className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand hover:underline">
              See the next lab
              <ArrowUpRight size={13} />
            </a>
          </div>

          <div
            className="rounded-2xl bg-white border border-[var(--line)] p-7 sm:p-8 relative overflow-hidden"
            data-testid="wb-youth-track"
          >
            <div className="absolute top-0 left-0 h-1 w-full bg-ink" />
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 rounded-lg bg-bone border border-[var(--line)] grid place-items-center">
                <GraduationCap size={19} className="text-ink" strokeWidth={2.1} />
              </div>
              <div>
                <div className="eyebrow !text-ink/60 !mb-0">For grades 6&ndash;12</div>
                <div className="font-display text-[20px] tracking-[-0.02em] text-ink leading-tight mt-0.5">
                  AI Build Lab: Student Camp.
                </div>
              </div>
            </div>
            <p className="text-[15px] leading-[1.65] text-ink/70 mb-5">
              A six-week, in-person after-school program where middle and high school students use
              AI to solve a real-world problem and build an app or website of their own.
            </p>
            <ul className="space-y-1.5 text-[13.5px] text-ink/70 mb-6">
              <li>&bull; Hosted by approved schools</li>
              <li>&bull; Parent-paid, funded, or sponsored</li>
              <li>&bull; Culminates in a Student Innovation Showcase</li>
            </ul>
            <Link
              to="/student-camp"
              data-testid="wb-youth-link"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink hover:underline"
            >
              Learn about the Student Camp
              <ArrowUpRight size={13} />
            </Link>
          </div>
        </div>

        {/* --- AI Build Lab pillars · what else Freedom In Me does --- */}
        <div className="mt-20 lg:mt-24">
          <div className="max-w-3xl">
            <div className="text-[10px] uppercase font-bold tracking-[0.22em] mb-4 text-brand">Also from AI Build Lab</div>
            <h3
              className="font-black uppercase leading-[1.05] tracking-[-0.02em] text-ink"
              style={{ fontSize: "clamp(28px, 3.5vw, 42px)", fontFamily: "'Inter', -apple-system, 'Segoe UI', system-ui, sans-serif" }}
            >
              Building an <span className="text-brand">AI-ready workforce.</span>
            </h3>
            <p className="mt-4 text-[15px] leading-[1.65] text-ink/65 max-w-xl">
              Beyond the monthly lab, we deliver AI training and consulting tailored to
              nonprofits, schools, small businesses, and executive teams.
            </p>
          </div>

          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5" data-testid="wb-pillars">
            {PILLARS.map((p) => (
              <Link
                key={p.key}
                to={p.to}
                data-testid={`wb-pillar-${p.key}`}
                className="group rounded-2xl bg-white border border-[var(--line)] p-6 flex flex-col hover:border-brand/50 hover:shadow-md transition-all"
              >
                <div className="w-10 h-10 rounded-lg bg-brand-soft grid place-items-center mb-4 group-hover:bg-brand transition-colors">
                  <p.Icon size={18} className="text-brand group-hover:text-white transition-colors" strokeWidth={2.1} />
                </div>
                <div className="text-[10px] uppercase font-bold tracking-[0.18em] text-ink/50 mb-1.5">{p.eyebrow}</div>
                <h4 className="font-display text-[18px] tracking-[-0.02em] text-ink leading-tight">{p.title}</h4>
                <p className="mt-3 text-[13px] leading-[1.6] text-ink/65 flex-1">{p.body}</p>
                <div className="mt-4 inline-flex items-center gap-1 text-[12px] font-semibold text-brand">
                  Ask about this
                  <ArrowUpRight size={12} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Closing line */}
        <p className="mt-10 text-sm text-ink/55 max-w-2xl">
          Freedom In Me, LLC &middot; Charlotte, North Carolina &middot;
          Helping employers prepare for the future of work while helping students prepare to become its future workforce.
        </p>
      </div>
    </section>
  );
}

